"use client";
import React, { useState } from "react";
import { useHolidays } from "../../context/HolidayContext";
import { Edit, Trash2, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import * as XLSX from "xlsx";

export default function Holidays() {
  const {
    holidays,
    loading,
    addHoliday,
    updateHoliday,
    deleteHoliday,
    toggleStatusHoliday,
  } = useHolidays();

  const { canDo } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterYear, setFilterYear] = useState("All");

  const itemsPerPage = 5;
  const fileInputRef = React.useRef(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id || null;

  // 🔍 Filter
  const filtered = holidays.filter((h) => {
    const matchesSearch =
      h.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      h.day?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesYear =
      filterYear === "All" || h.date?.startsWith(filterYear);

    return matchesSearch && matchesYear;
  });

  // 📄 Pagination
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + rowsPerPage);

  // ➕ Add
  const handleAdd = async (formData) => {
    const payload = {
      ...formData,
      create_audit_id: userId,
    };

    const res = await addHoliday(payload);
    if (res.success) setShowModal(false);
    else alert(res.message);
  };

  // ✏️ Edit
  const handleEdit = async (formData) => {
    const payload = {
      ...formData,
      update_audit_id: userId,
    };

    const res = await updateHoliday(selectedHoliday.id, payload);
    if (res.success) setEditModal(false);
    else alert(res.message);
  };

  // 🗑 Delete
  const handleDelete = async () => {
    const res = await deleteHoliday(selectedHoliday.id, userId);
    if (res.success) setDeleteModal(false);
    else alert(res.message);
  };

 

  if (loading) return <p className="text-center mt-6">Loading...</p>;

  return (
    <div className="bg-sky-50 px-9 py-6 max-sm:px-4">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
        Holidays
      </h2>

      {/* 🔍 Search + Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white px-3 py-3 rounded-xl shadow-sm">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center border border-gray-300 rounded-lg px-3 py-1.5 w-full sm:w-80 transition-all focus-within:ring-2 focus-within:ring-[#2563eb]/40 focus-within:border-[#2563eb]/40">
            <input
              type="text"
              placeholder="Search holiday..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              className="outline-none text-sm w-full"
            />

            <select
              value={filterYear}
              onChange={(e) => {
                setFilterYear(e.target.value);
                setPage(1);
              }}
              className="text-sm outline-none border-l pl-2 ml-2"
            >
              <option value="All">All</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
            </select>
          </div>

          {(searchQuery || filterYear !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setFilterYear("All");
                setPage(1);
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>

        {canDo("Holidays", "Add") && (
          <div className="flex gap-2">
            
            <button
              onClick={() => setShowModal(true)}
              // className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm"
              className="px-4 py-2  text-white rounded-lg text-sm"
          style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}  >
              + Add Holiday
            </button>

            <button
              onClick={() => setUploadModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm"
            >
              Upload File
            </button>

          </div>
        )}
      </div>

      {/* 📋 Table */}
      <div className="bg-white rounded-2xl shadow-sm p-6 overflow-x-auto">
        {filtered.length === 0 ? (
          <p className="text-center text-gray-500 py-6">
            No holidays found.
          </p>
        ) : (
          <table className="min-w-full table">
            <thead>
              <tr className="bg-sky-100 text-sm">
                <th className="py-3 px-4 text-left">ID</th>
                <th className="py-3 px-4 text-left">Day</th>
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-left">Holiday</th>
                <th className="py-3 px-4 text-left">Status</th>
                {(canDo("Holidays", "Edit") || canDo("Holidays", "Delete")) && (
                  <th className="py-3 px-4 text-center">Actions</th>
                )}
              </tr>
            </thead>

            <tbody>
              {filtered.map((h, i) => (
                <tr key={h.id} className="border-b text-sm">
                  <td className="px-4 py-3">{(page - 1) * rowsPerPage + i + 1}</td>
                  <td className="px-4 py-3 uppercase">{h.day}</td>
                  <td className="px-4 py-3">
                    {new Date(h.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 uppercase">{h.name}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleStatusHoliday(h.id, h.status)}
                      className={`px-2 py-1 rounded-full text-xs font-medium transition-colors duration-200 ${
                        h.status === "active"
                          ? "bg-green-100 text-green-700 hover:bg-green-200"
                          : "bg-red-100 text-red-700 hover:bg-red-200"
                      }`}
                    >
                      {h.status === "active" ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex justify-center gap-3 text-gray-600">
                      {canDo("Holidays", "Edit") && (
                      <Edit size={16} className="cursor-pointer hover:text-indigo-600" onClick={() => {
                          setSelectedHoliday(h);
                          setEditModal(true);
                        }} />
                      )}
                      {canDo("Holidays", "Delete") && (
                      <Trash2 size={16} className="cursor-pointer hover:text-red-600" onClick={() => {
                          setSelectedHoliday(h);
                          setDeleteModal(true);
                        }} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 📄 Pagination */}
      

      {/* ➕ Add Modal */}
      {showModal && (
        <Modal title="Add Holiday" onClose={() => setShowModal(false)}>
          <HolidayForm onSubmit={handleAdd} onCancel={() => setShowModal(false)} />
        </Modal>
      )}

      {/* ✏️ Edit Modal */}
      {editModal && (
        <Modal title="Edit Holiday" onClose={() => setEditModal(false)}>
          <HolidayForm
            holiday={selectedHoliday}
            onSubmit={handleEdit}
            onCancel={() => setEditModal(false)}
          />
        </Modal>
      )}

      {/* 🗑 Delete Modal */}
      {deleteModal && (
        <Modal title="Delete Holiday" onClose={() => setDeleteModal(false)}>
          <p className="mb-4">
            Delete <b>{selectedHoliday?.name}</b>?
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteModal(false)}>Cancel</button>
            <button onClick={handleDelete} className="bg-red-600 text-white px-3 py-1 rounded">
              Delete
            </button>
          </div>
        </Modal>
      )}

      {uploadModal && (
        <Modal title="Upload Holidays" onClose={() => setUploadModal(false)}>
          <HolidayFileUpload
            onClose={() => setUploadModal(false)}
            userId={userId}
            addHoliday={addHoliday}
          />
        </Modal>
      )}
    </div>
  );
}


const convertExcelDate = (value) => {
  if (!value) return null;

  // If already ISO or normal string
  if (typeof value === "string" && value.includes("-")) {
    return value;
  }

  // If Excel serial number
  if (typeof value === "number") {
    const date = new Date((value - 25569) * 86400000);
    return date.toISOString().split("T")[0];
  }

  // If string like 4/9/2026
  const date = new Date(value);
  if (!isNaN(date)) {
    return date.toISOString().split("T")[0];
  }

  return null;
};

function HolidayFileUpload({ onClose, userId, addHoliday }) {
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState([]);
  const fileInputRef = React.useRef(null);
  const { fetchHolidays } = useHolidays();

  // Parse file on selection for preview + validation
  const handleFileChange = (e) => {
  const selected = e.target.files[0];
  if (!selected) return;

  // ✅ FILE TYPE VALIDATION
  const allowedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ];

  if (!allowedTypes.includes(selected.type)) {
    alert("Only .xlsx Excel files are allowed");
    e.target.value = "";
    setFile(null);
    setPreview([]);
    return;
  }

  // optional extra check (file extension safety)
  if (!selected.name.endsWith(".xlsx")) {
    alert("Invalid file format. Please upload .xlsx file only");
    e.target.value = "";
    setFile(null);
    setPreview([]);
    return;
  }

  setFile(selected);

  const reader = new FileReader();
  reader.onload = (evt) => {
    try {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      const map = new Map();
      json.forEach((row) => {
        const date = convertExcelDate(row.date);
        if (!date) return;
        if (!map.has(date)) {
          map.set(date, {
            name: row.name || "",
            date,
            day: row.day || "",
            status: "active",
            create_audit_id: userId,
          });
        }
      });

      setPreview(Array.from(map.values()));
    } catch {
      alert("Could not parse file. Please check Excel format.");
      setFile(null);
    }
  };

  reader.readAsArrayBuffer(selected);
};

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file");
      return;
    }
    if (preview.length === 0) {
      alert("No valid rows found in file. Check column names: name, date, day");
      return;
    }

    setLoading(true);

    try {
      // Use allSettled so one failure doesn't block others
      const results = await Promise.allSettled(
        preview.map((item) => addHoliday(item))
      );

      const succeeded = results.filter(
        (r) => r.status === "fulfilled" && r.value?.success
      );
      const failed = results.filter(
        (r) => r.status === "rejected" || !r.value?.success
      );

      if (failed.length > 0) {
        const messages = failed.map((r) =>
          r.status === "rejected"
            ? r.reason?.message || "Unknown error"
            : r.value?.message || "Failed"
        );
        alert(
          `⚠️ ${succeeded.length} added, ${failed.length} failed:\n` +
          messages.join("\n")
        );
      } else {
        alert(`✅ ${succeeded.length} Holidays Added Successfully`);
      }

      if (succeeded.length > 0) {
        // Refresh list — works whether context exports it or not
        if (typeof fetchHolidays === "function") await fetchHolidays();

        setFile(null);
        setPreview([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        onClose();
      }
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        File must have columns: <code>name</code>, <code>date</code>,{" "}
        <code>day</code>
      </p>

      <p className="text-xs text-red-500 mb-1 font-medium">
  ⚠️ Only Excel files (.xlsx) are accepted
</p>

<input
  ref={fileInputRef}
  type="file"
  accept=".xlsx"
  onChange={handleFileChange}
  className="w-full border p-2 rounded outline-none transition-all focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40"
/>

      {/* Preview parsed rows */}
      {preview.length > 0 && (
        <div className="max-h-40 overflow-y-auto border rounded p-2 text-xs text-gray-700">
          <p className="font-semibold mb-1">{preview.length} rows ready to upload:</p>
          {preview.slice(0, 10).map((row, i) => (
            <div key={i}>
              {row.date} — {row.name}
            </div>
          ))}
          {preview.length > 10 && (
            <p className="text-gray-400">...and {preview.length - 10} more</p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={loading || preview.length === 0}
          className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {loading ? "Uploading..." : `Upload ${preview.length > 0 ? `(${preview.length})` : ""}`}
        </button>
      </div>
    </div>
  );
}

/* 🔹 Modal */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
      <div className="bg-white p-6 rounded-xl w-full max-w-md relative">
        <button onClick={onClose} className="absolute right-4 top-2">✕</button>
        <h2 className="mb-4 font-bold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/* 🔸 Form */
function HolidayForm({ holiday = {}, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    name: holiday.name || "",
    date: holiday.date || "",
    status: holiday.status || "active",
  });

  const handleSubmit = (e) => {
  e.preventDefault();

  if (!formData.name || !formData.date) {
    alert("Please fill all fields");
    return;
  }

  const day = new Date(formData.date).toLocaleDateString("en-US", {
    weekday: "long",
  });

  onSubmit({
    ...formData,
    day,
  });
};

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <input
        placeholder="Holiday Name"
        value={formData.name}
        onChange={(e) =>
          setFormData({ ...formData, name: e.target.value })
        }
        className="w-full border p-2 rounded outline-none transition-all focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40"
        required
      />

      <input
        type="date"
        value={formData.date}
        onChange={(e) =>
          setFormData({ ...formData, date: e.target.value })
        }
        className="w-full border p-2 rounded outline-none transition-all focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40"
        required
      />

      <select
        value={formData.status}
        onChange={(e) =>
          setFormData({ ...formData, status: e.target.value })
        }
        className="w-full border p-2 rounded outline-none transition-all focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40"
      >
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button className=" text-white px-4 py-2 rounded"
        style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}>
          Save
        </button>
      </div>
    </form>
  );
}